import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'
import * as data from '../data'

import { Dimension } from './dimension'
import type { Entity } from './entity'
import { ForRecord } from './for-record'
import { Id as _Id } from './id'
import { Identifiable as _Identifiable } from './identifiable'
import { Model } from './model'
import type { Schema } from './schema'
import type { Snapshot } from './snapshot'
import { Term } from './term'
import { Type as _Type } from './type'

type AttributeBase<TId extends Attribute.Id = Attribute.Id> = Model<TId> &
  common.PotentiallyOptional
/**
 * Represents a property or field of an {@link Entity}. In other words, contains
 * metadata about the fields of a {@link data.Record}
 */
export type Attribute<TId extends Attribute.Id = Attribute.Id> =
  AttributeBase<TId> & {
    /**
     * @see {@link Attribute.Type}
     */
    type: Attribute.Type
    /**
     * When `false`, this attribute is not allowed to be marked as optional
     */
    canBeOptional?: boolean
    // TODO: maybe use JSON schema instead and have it be bundled in with the
    // type so that you basically have a constrained type or type with schema.
    // This is most relevant for structures, but could be useful for other types
    // as well
    constraints?: common.value.Constraints
    /**
     * When provided, this is used to compute the value for the
     * {@link Attribute}, meaning the value for this {@link Attribute} will come
     * from the {@link Attribute.Computation} not any sort of external input. In
     * other words, the value should be treated as readonly from the user's
     * perspective
     */
    computation?: Attribute.Computation
    default?: unknown
    /**
     * @see {@link Attribute.Scoring}
     */
    scoring?: Attribute.Scoring
  }
export namespace Attribute {
  export const isAttribute: TypeChecker<Attribute> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.intersection(
        Model.getTypeChecker(Id.isId),
        common.PotentiallyOptional.isPotentiallyOptional,
        common.Hideable.isHideable,
        TypeChecker.Factory.object<Difference<Attribute, AttributeBase>>({
          canBeOptional: TypeChecker.Factory.optional(TypeChecker.isBoolean),
          computation: Computation.isComputation,
          constraints: common.value.Constraints.isConstraints,
          default: TypeChecker.isUnknown,
          scoring: Scoring.isScoring,
          type: Type.isType,
        }),
      ),
  ).usingAlias('Attribute')
  /**
   * Just checks if the object's ID indicates that it is an attribute. This can
   * be used to distinguish between various model types without checking that
   * every field of the model is correct.
   */
  export const shouldBeAttribute = TypeChecker.Factory.lazy(() =>
    Model.getLooseTypeChecker(_Type.Attribute),
  )

  /**
   * Converts a DMS {@link Attribute} to a common {@link Attribute}; e.g., for
   * use in a table
   */
  export function toCommonAttribute(
    attribute: Attribute,
    schema?: Schema,
  ): common.Attribute {
    return {
      description: attribute.description,
      id: attribute.id,
      isHidden: false,
      name: attribute.name,
      valueInfo: {
        type: data.Value.Type.toCommonValueType(attribute.type, schema),
      },
    } satisfies NoMissing<common.Attribute>
  }

  /**
   * Indicates that the given {@link Attribute} is associated with the given
   * {@link Id.Context.Source}
   */
  export function isOn(
    attribute: Attribute.Identifiable,
    source: Id.Context.Source,
  ): boolean {
    return Id.Context.from(attribute) === Id.Context.from(source)
  }

  /**
   * Represents a type suitable for use in {@link models.Attribute}.
   */
  export type Type = Exclude<
    data.Value.Type,
    | data.Value.Type.Enum.Inline
    | data.Value.Type.Structure.Inline
    | data.Value.Type.Reference.Multi
  >
  export namespace Type {
    export const isType: TypeChecker<Type> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.exclude(
        isType,
        TypeChecker.Factory.union(
          data.Value.Type.Enum.Inline.isInline,
          data.Value.Type.Structure.Inline.isInline,
          data.Value.Type.Reference.Multi.isMulti,
        ),
      ),
    )
  }

  /**
   * Information needed to create an {@link Attribute}
   */
  export type Input = Omit<Attribute, 'id' | 'terms'> & {
    context: Attribute.Id.Context
    partialId?: PartialId
    termIds?: readonly Term.Id[]
  }
  export namespace Input {
    export const isInput: TypeChecker<Input> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.intersection(
        TypeChecker.Factory.omit(isAttribute, ['id']),
        TypeChecker.Factory.object<Difference<Input, Attribute>>({
          context: Attribute.Id.Context.isContext,
          partialId: TypeChecker.Factory.optional(PartialId.isPartialId),
          termIds: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Term.Id.isId),
          ),
        }),
      ),
    ).usingAlias('Attribute.Input')

    export const FOR_ID = {
      canBeOptional: false,
      description: 'System identifier',
      isOptional: false,
      name: 'ID',
      partialId: PartialId.Standard.create('id'),
      type: common.value.types.Intrinsic.Text,
    } as const satisfies InContext

    /**
     * Converts an {@link Attribute} to an {@link Input}
     */
    export function from(attribute: Readonly<Attribute>): Input {
      const { context, partialId } = _Id.Parts.from(attribute.id)
      return {
        canBeOptional: attribute.canBeOptional,
        computation: attribute.computation,
        constraints: attribute.constraints,
        // NOTE: because the Word portion of the Id is typed as `string`,
        // TypeScript thinks there are a variety of possible ways to parse the
        // Id, in which context doesn't end up being what we expected. However,
        // it is pretty safe for us to assume that the Word portion will always
        // be an actual Word, which means the context will be parsed correctly
        context:
          context satisfies _Id.Context.Of<Attribute.Id> as Attribute.Id.Context,
        default: attribute.default,
        description: attribute.description,
        isOptional: attribute.isOptional,
        name: attribute.name,
        note: attribute.note,
        partialId,
        scoring: attribute.scoring
          ? {
              default: attribute.scoring.default,
              lookup: attribute.scoring.lookup,
            }
          : undefined,
        termIds: attribute.terms?.map(term => term.id),
        type: attribute.type,
      } satisfies NoMissing<Input>
    }

    /**
     * Attributes that are already in a {@link Attribute.Context} (e.g. inputted
     * on an {@link Attribute.Holder}) do not need to specify their associated
     * {@link Attribute.Context}
     */
    export type InContext = Omit<Input, 'context'>
    export namespace InContext {
      export const isInContext: TypeChecker<InContext> =
        TypeChecker.Factory.omit(isInput, ['context'])
    }
  }

  /**
   * A {@link Model} that can have {@link Attribute}
   */
  export type Holder = ForRecord | Dimension
  export namespace Holder {
    /**
     * Valid {@link Type} for an {@link Attribute.Holder}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Type = _Id.Parts.Of<Id>['type']
    export namespace Type {
      const _ALL = [_Type.Entity, _Type.Snapshot, _Type.Dimension] as const
      /** Ensures we aren't missing anything in {@link _ALL} */
      type VerifyAll = Satisfies<Type, (typeof _ALL)[number]>

      export const ALL: readonly Type[] = _ALL

      export const isType: TypeChecker<Type> = TypeChecker.Factory.oneOf(ALL)
    }

    /**
     * ID of an {@link Attribute.Holder}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Id = ForRecord.Id | Dimension.Id
    export namespace Id {
      export const isId: TypeChecker<Id> = TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.union(ForRecord.Id.isId, Dimension.Id.isId),
      )

      export function from(
        identifiable: Exclude<
          Extract<Entity.Identifiable, ForRecord.Identifiable>,
          Dimension.Identifiable
        >,
      ): Entity.Id
      export function from(
        identifiable: Exclude<
          Extract<Snapshot.Identifiable, ForRecord.Identifiable>,
          Dimension.Identifiable
        >,
      ): Snapshot.Id
      export function from(
        identifiable: Exclude<Dimension.Identifiable, ForRecord.Identifiable>,
      ): Dimension.Id
      export function from(
        identifiable: Exclude<ForRecord.Identifiable, Dimension.Identifiable>,
      ): ForRecord.Id
      export function from(identifiable: Identifiable): Id
      export function from(identifiable: Attribute.Identifiable): Id | undefined
      export function from(
        identifiable: Identifiable | Attribute.Identifiable,
      ): Id | undefined {
        if (Attribute.Identifiable.isIdentifiable(identifiable)) {
          // If this is an attribute, then we retrieve the holder from the
          // context, if we can
          const context = Attribute.Id.Context.from(identifiable)
          if (Attribute.Id.Context.ForAll.isForAll(context)) {
            // We cannot retrieve a holder ID if the context is not tied to a
            // specific holder
            return undefined
          }
          // If it is, we just need to standardize the ID
          return _Id.Separator.swap(
            context,
            _Id.Separator.WithinSection,
            _Id.Separator.BetweenSections,
          )
        }
        // If we were not given an attribute, then we were given the
        // identifiable of a holder. We just need to get the ID of that holder
        if (ForRecord.Identifiable.isIdentifiable(identifiable)) {
          return ForRecord.Id.from(identifiable)
        }
        return Dimension.Id.from(identifiable)
      }
    }

    /**
     * Can be used to get the ID of an {@link Attribute.Holder}
     *
     * __NOTE:__ be cautious as use of this type can violate the
     * {@link https://en.wikipedia.org/wiki/Liskov_substitution_principle Liskov substitution principle}.
     * {@link Context.from} is an example of this. Unfortunately, there is no
     * easy way to avoid this
     *
     * @see {@link Attribute.Holder.Id.from}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Identifiable =
      // NOTE: These overlap (entities and snapshots can both be used to
      // identify a dimension). However, we cannot eliminate this overlap as
      // that would eliminate everything that can identify entities and
      // snapshots. See more details in the JSDoc above
      ForRecord.Identifiable | Dimension.Identifiable
    export namespace Identifiable {
      export const isIdentifiable: TypeChecker<Identifiable> =
        TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.union(
            ForRecord.Identifiable.isIdentifiable,
            Dimension.Identifiable.isIdentifiable,
          ),
        )
    }
  }

  export type Identifiable<
    TPartialId extends PartialId = PartialId,
    TContext extends Id.Context = Id.Context,
  > = _Identifiable.ForSpecificType<_Type.Attribute, TPartialId, TContext>
  export namespace Identifiable {
    export const isIdentifiable: TypeChecker<Identifiable> =
      TypeChecker.Factory.lazy(() =>
        _Identifiable.ForSpecificType.getTypeChecker(
          _Type.Attribute,
          Id.Context.isContext,
        ),
      ).usingAlias('dms.models.Attribute.Identifiable')
  }

  export type Id<
    TPartialId extends PartialId = PartialId,
    TContext extends Id.Context = Id.Context,
  > = _Id<_Type.Attribute, TPartialId, TContext>
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.lazy(() =>
      _Id.getTypeChecker(_Type.Attribute, Context.isContext),
    ).usingAlias('Attribute.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

    export type Of<TIdentifiable extends Identifiable> =
      TIdentifiable extends Identifiable<infer TPartialId, infer TContext>
        ? Id<TPartialId, TContext>
        : never

    export function from<TIdentifiable extends Identifiable>(
      source: TIdentifiable,
    ): Of<TIdentifiable>
    export function from(source: Identifiable): Id {
      return _Id.from<_Type.Attribute, PartialId, Context>(
        _Identifiable.ForSpecificType.bind(source, _Type.Attribute),
      )
    }

    // Using a strict type results in an enormous union to cover all attributes
    // on all possible entities, which causes a bunch of problems for TypeScript
    export type Context =
      // For all models of a given type
      | Context.ForAll
      // For a specific model
      | Context.Using<Holder.Id>
    export namespace Context {
      export const isContext: TypeChecker<Context> = TypeChecker.Factory.lazy(
        () =>
          TypeChecker.Factory.union(
            ForAll.isForAll,
            Using.isUsing,
          ) as TypeChecker<Context>,
      ).usingAlias('Attribute.Id.Context')
      export const assertIsContext: TypeAsserter<Context> =
        TypeAsserter.Factory.from(isContext)

      /**
       * Represents an {@link Attribute} that appears on every
       * {@link ForRecord} of the given {@link ForRecord.Type}
       */
      export type ForAll<TType extends ForRecord.Type = ForRecord.Type> =
        `${typeof ForAll.PREFIX}${TType}`
      export namespace ForAll {
        export const PREFIX = 'all-'

        export const isForAll: TypeChecker<ForAll> = TypeChecker.Factory.custom(
          (val: unknown): val is ForAll =>
            typeof val === 'string' &&
            val.startsWith(PREFIX) &&
            ForRecord.Type.isType(val.substring(PREFIX.length)),
          {
            typeDescription: 'Attribute.Id.Context.ForAll',
          },
        )

        export function of<TType extends ForRecord.Type>(
          type: TType,
        ): ForAll<TType> {
          return `${PREFIX}${type}`
        }
      }

      export type Using<THolderId extends Holder.Id> = _Id.UsingSeparator.On<
        THolderId,
        _Id.Separator.WithinSection
      >
      export namespace Using {
        export function getTypeChecker<THolderId extends Holder.Id>(
          isHolderId: TypeChecker<THolderId>,
        ): TypeChecker<Using<THolderId>> {
          return _Id.UsingSeparator.On.getTypeChecker(
            _Id.Separator.WithinSection,
            isHolderId,
          )
        }

        export const isUsing = TypeChecker.Factory.lazy(() =>
          getTypeChecker(Holder.Id.isId),
        ).usingAlias('Attribute.Id.Context.ForAll.Using')
      }

      /**
       * {@link Attribute.Id.Context} can come from a
       * {@link Holder.Identifiable} (through
       * {@link Attribute.Id.Context.Using}) or a {@link ForRecord.Type}
       * (through {@link Attribute.Id.Context.ForAll})
       */
      export type Source = Holder.Identifiable | ForRecord.Type | Context

      // TODO: maybe refactor this so it doesn't violate the Liskov substitution
      // principle
      /**
       * Produces an {@link Attribute.Context}
       *
       * __WARNING:__ this function violates the
       * {@link https://en.wikipedia.org/wiki/Liskov_substitution_principle Liskov substitution principle}.
       * For example:
       * ```ts
       * function getAttributesInDimension(dimension: Dimension.Identifiable): readonly Attribute[] {
       *   const desiredContext = Attribute.Id.Context.from(dimension)
       *   const attributes = getAllAttributes()
       *   return attributes.filter(attribute => Attribute.Id.Context.from(attribute) === desiredContext)
       * }
       *
       * declare const dimensionOrEntity: Dimension | Entity
       * // Uh oh! TypeScript does not see a problem here because both Dimension and Entity can be used to get a Dimension.Id.
       * // However, Attribute.Id.Context.from(entity) and Attribute.Id.Context.from(dimension) produce different results.
       * // This means we will not get the expected results here when dimensionOrEntity is an Entity
       * const attributes = getAttributesInDimension(dimensionOrEntity)
       * ```
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function from<TContext extends Context>(
        context: TContext,
      ): TContext
      export function from<TIdentifiable extends Identifiable>(
        attribute: TIdentifiable,
      ): Extract<_Id.Context.Of<Id.Of<TIdentifiable>>, Context>
      export function from<TEntityIdentifiable extends Entity.Identifiable>(
        entity: TEntityIdentifiable,
      ): Context.Using<Entity.Id.Of<TEntityIdentifiable>>
      export function from<TSnapshotIdentifiable extends Snapshot.Identifiable>(
        snapshot: TSnapshotIdentifiable,
      ): Context.Using<Snapshot.Id.Of<TSnapshotIdentifiable>>
      export function from<
        TDimensionIdentifiable extends Dimension.Identifiable,
      >(
        dimension: TDimensionIdentifiable,
      ): Context.Using<Dimension.Id.Of<TDimensionIdentifiable>>
      export function from<TType extends ForRecord.Type>(
        source: TType,
      ): Context.ForAll<TType>
      export function from(source: Identifiable | Source): Context
      export function from<TType extends ForRecord.Type>(
        source: Identifiable | Source,
      ): Context | Context.ForAll<TType> {
        if (Context.isContext(source)) {
          return source
        }
        if (ForRecord.Type.isType(source)) {
          return ForAll.of(source)
        }
        if (Identifiable.isIdentifiable(source)) {
          // Casting is needed because having template string types means weird
          // things can get picked up for the Context (could be solved if
          // TypeScript ever supports regular expression types)
          return _Id.Context.from(Id.from(source)) as Context
        }
        return _Id.Separator.swap(
          Holder.Id.from(source),
          _Id.Separator.BetweenSections,
          _Id.Separator.WithinSection,
        )
      }
    }
  }

  /**
   * Used to compute a value for an {@link Attribute}
   *
   * This is a {@link data.query.Query} that will automatically target the model
   * and record that contains the associated {@link Attribute}
   *
   * The return value of the {@link Computation.selection} will be applied as
   * the value of the {@link Attribute}
   *
   * @example
   * // Attribute: Full Name
   * {
   *   selection: {
   *     '++': [
   *       { '@.': '!first' },
   *       ' ',
   *       { '@.': '!last' },
   *     ]
   *   }
   * }
   *
   * // Attribute: Total Expenses
   * {
   *   selection: {
   *     '+': { '@': 'expenses' }
   *   },
   *   subqueries: {
   *     expenses: { ... }
   *   }
   * }
   */
  export interface Computation {
    // TODO: maybe add other properties like lastRan or error
    definition: Computation.Definition
  }
  export namespace Computation {
    export const isComputation: TypeChecker<Computation> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.object<Computation>({
          definition: Definition.isDefinition,
        }),
      )

    /**
     * Used to compute a value for an {@link Attribute}
     *
     * This is a {@link data.query.Query} that will automatically target the model
     * and record that contains the associated {@link Attribute}
     *
     * The return value of the {@link Computation.selection} will be applied as
     * the value of the {@link Attribute}
     *
     * @example
     * // Attribute: Full Name
     * {
     *   selection: {
     *     '++': [
     *       { '@.': '!first' },
     *       ' ',
     *       { '@.': '!last' },
     *     ]
     *   }
     * }
     *
     * // Attribute: Total Expenses
     * {
     *   selection: {
     *     '+': { '@': 'expenses' }
     *   },
     *   subqueries: {
     *     expenses: { ... }
     *   }
     * }
     */
    export type Definition = Required.Semi<
      Pick<data.query.Query, 'selection' | 'subqueries'>,
      'selection'
    >
    export namespace Definition {
      export const isDefinition: TypeChecker<Definition> =
        TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.object<Definition>({
            selection: data.expression.Expression.isExpression,
            subqueries: TypeChecker.Factory.optional(
              data.query.Subquery.Lookup.isLookup,
            ),
          }),
        )
    }

    /**
     * Used to identify a set of {@link Computation}, or more precisely,
     * {@link Attribute} that have an {@link Attribute.computation}
     */
    export type Selector =
      | 'all'
      | MutuallyExclusive<
          [
            {
              /**
               * Applies to all {@link Attribute.computation} that use data from the
               * given {@link ForRecord}
               */
              readonly using: Maybe.ReadonlyArray<ForRecord.Id>
            },
            // NOTE: we need to separate these two since there are some
            // Attribute.Identifiable that are also an array
            {
              /**
               * Simply identifies the {@link Attribute} that is desired
               */
              readonly attribute: Attribute.Identifiable
            },
            {
              /**
               * Simply identifies the list of {@link Attribute} that are
               * desired
               */
              readonly attributes: readonly Attribute.Identifiable[]
            },
          ]
        >
  }

  /**
   * Scoring configuration for the attribute. This is used to determine how
   * to score the attribute values.
   */
  export interface Scoring {
    /**
     * Associates numeric scores with attribute values. For example, enum values
     * can be assigned different scores.
     *
     * __NOTE:__ The structure of lookup will be usually determined by the
     * {@link Attribute.type} property.
     *
     * @default undefined
     */
    // NOTE: Store all keys as strings, since TypeScript only allows numbers,
    // strings, or symbols as object keys
    lookup?: Partial<Record<string, number>>
    /**
     * Default scoring value used when a value has no specific score assigned in
     * lookup, or lookup is not defined
     *
     */
    default: number
  }
  export namespace Scoring {
    export const isScoring: TypeChecker<Scoring> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.object<Scoring>({
          default: TypeChecker.isNumber,
          lookup: TypeChecker.Factory.optional(
            TypeChecker.Factory.record(
              TypeChecker.isString,
              TypeChecker.isNumber,
            ),
          ),
        }),
    ).usingAlias('Attribute.Scoring')

    export const assertIsScoring: TypeAsserter<Scoring> =
      TypeAsserter.Factory.from(isScoring)
  }
}
